import { call, put, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import {
  engagementFormSagaActionTypes,
  submitFormEngagementSagaActionPayloadType,
  updateFormEngagementSagaActionPayloadType,
  reviewEngagementSagaActionPayloadType,
  deleteEngagementSagaActionPayloadType,
  getEngagementSubmissionsSagaAction,
} from "./saga-actions";
import {
  EngagementSubmission,
  FormFieldType,
} from "../../slices/engagement-form/Types";
import { engagementFormSliceAction } from "../../slices/engagement-form";

type getFormFieldsEngagementAPIResponseType = {
  success: boolean;
  message: string;
  data: FormFieldType[];
};
const getFormFieldsEngagementAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getFormFieldsEngagementAPIResponseType>>(
      "/formField/Engagement"
    )
    .then((res) => res.data);
  return response;
};

type submitFormEngagementAPIResponseType = {
  success: boolean;
  message: string;
};

const submitFormEngagementAPI = async (
  payload: submitFormEngagementSagaActionPayloadType
) => {
  let response = axiosRestInstance
    .post<AxiosResponse<submitFormEngagementAPIResponseType>>("/form", payload)
    .then((res) => res.data);
  return response;
};

const updateFormEngagementAPI = async (
  payload: updateFormEngagementSagaActionPayloadType
) => {
  let response = axiosRestInstance
    .put<AxiosResponse<submitFormEngagementAPIResponseType>>(
      `/form/update/${payload.engagement_id}`,
      payload
    )
    .then((res) => res.data);
  return response;
};

type getEngagementSubmissionsAPIResponseType = {
  success: true;
  message: string;
  data: EngagementSubmission[];
};
const getEngagementSubmissionsAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getEngagementSubmissionsAPIResponseType>>(
      "/form/all/Engagement"
    )
    .then((res) => res.data);
  return response;
};

type getEngagementByIdAPIResponseType = {
  success: boolean;
  message: string;
  data: EngagementSubmission;
};
const getEngagementByIdAPI = async (engagementId: string) => {
  let response = axiosRestInstance
    .get<AxiosResponse<getEngagementByIdAPIResponseType>>(
      `/form/${engagementId}`
    )
    .then((res) => res.data);
  return response;
};

const reviewEngagementAPI = async (payload: reviewEngagementSagaActionPayloadType) => {
  let response = axiosRestInstance
    .put<AxiosResponse<submitFormEngagementAPIResponseType>>(
      `/form/review/${payload.engagementId}`,
      { review_status: payload.review_status }
    )
    .then((res) => res.data);
  return response;
};

const deleteEngagementAPI = async (engagementId: string) => {
  let response = axiosRestInstance
    .delete<AxiosResponse<submitFormEngagementAPIResponseType>>(
      `/form/${engagementId}`
    )
    .then((res) => res.data);
  return response;
};

// ----------------------- API Above

function* getFormFieldsEngagementSaga(action: { type: string }) {
  try {
    yield put(engagementFormSliceAction.setFormFieldsEngagementLoading(true));
    let response: getFormFieldsEngagementAPIResponseType = yield call(
      getFormFieldsEngagementAPI
    );
    yield put(
      engagementFormSliceAction.setFormFieldsEngagement(response.data || [])
    );
  } catch (e) {
    console.log(e);
  } finally {
    yield put(engagementFormSliceAction.setFormFieldsEngagementLoading(false));
  }
}

function* submitFormEngagementSaga(action: {
  type: string;
  payload: submitFormEngagementSagaActionPayloadType;
}) {
  try {
    yield put(engagementFormSliceAction.setSubmitFormEngagementLoading(true));
    let response: submitFormEngagementAPIResponseType = yield call(
      submitFormEngagementAPI,
      action.payload
    );
    yield put(engagementFormSliceAction.setSubmitFormEngagementStatus(true));
    yield put(
      engagementFormSliceAction.setSubmitFormEngagementMessage(response.message)
    );
  } catch (e: any) {
    yield put(engagementFormSliceAction.setSubmitFormEngagementStatus(false));
    yield put(
      engagementFormSliceAction.setSubmitFormEngagementMessage(
        e.message || "Something went wrong"
      )
    );
    console.log(e);
  } finally {
    yield put(engagementFormSliceAction.setSubmitFormEngagementLoading(false));
  }
}

function* updateFormEngagementSaga(action: {
  type: string;
  payload: updateFormEngagementSagaActionPayloadType;
}) {
  try {
    yield put(engagementFormSliceAction.setSubmitFormEngagementLoading(true));
    let response: submitFormEngagementAPIResponseType = yield call(
      updateFormEngagementAPI,
      action.payload
    );
    yield put(engagementFormSliceAction.setSubmitFormEngagementStatus(true));
    yield put(
      engagementFormSliceAction.setSubmitFormEngagementMessage(response.message)
    );
  } catch (e: any) {
    yield put(engagementFormSliceAction.setSubmitFormEngagementStatus(false));
    yield put(
      engagementFormSliceAction.setSubmitFormEngagementMessage(
        e.message || "Something went wrong"
      )
    );
    console.log(e);
  } finally {
    yield put(engagementFormSliceAction.setSubmitFormEngagementLoading(false));
  }
}

function* getEngagementSubmissionsSaga(action: { type: string }) {
  try {
    yield put(engagementFormSliceAction.setEngagementSubmissionsLoading(true));
    let response: getEngagementSubmissionsAPIResponseType = yield call(
      getEngagementSubmissionsAPI
    );
    yield put(
      engagementFormSliceAction.setEngagementSubmissions(response.data || [])
    );
  } catch (e) {
    console.log(e);
  } finally {
    yield put(engagementFormSliceAction.setEngagementSubmissionsLoading(false));
  }
}

function* getEngagementByIdSaga(action: { type: string; payload: string }) {
  try {
    yield put(engagementFormSliceAction.setEngagementByIdLoading(true));
    let response: getEngagementByIdAPIResponseType = yield call(
      getEngagementByIdAPI,
      action.payload
    );

    // Check if the data source is not "Engagement"
    if (response.data.source !== "Engagement") {
      yield put(engagementFormSliceAction.setEngagementById(null));
    } else {
      yield put(engagementFormSliceAction.setEngagementById(response.data));
    }
  } catch (e) {
    console.log(e);
  } finally {
    yield put(engagementFormSliceAction.setEngagementByIdLoading(false));
  }
}

function* reviewEngagementSaga(action: {
  type: string;
  payload: reviewEngagementSagaActionPayloadType;
}) {
  try {
    // Set loading state for this specific engagement
    yield put(engagementFormSliceAction.setReviewLoading({
      engagementId: action.payload.engagementId,
      loading: true
    }));
    
    let response: submitFormEngagementAPIResponseType = yield call(
      reviewEngagementAPI,
      action.payload
    );
    // Refresh the engagement submissions list after review
    yield put(getEngagementSubmissionsSagaAction());
  } catch (e: any) {
    console.log(e);
  } finally {
    // Clear loading state for this specific engagement
    yield put(engagementFormSliceAction.setReviewLoading({
      engagementId: action.payload.engagementId,
      loading: false
    }));
  }
}

function* deleteEngagementSaga(action: {
  type: string;
  payload: deleteEngagementSagaActionPayloadType;
}) {
  try {
    // Set loading state for this specific engagement
    yield put(engagementFormSliceAction.setDeleteLoading({
      engagementId: action.payload.engagementId,
      loading: true
    }));
    
    let response: submitFormEngagementAPIResponseType = yield call(
      deleteEngagementAPI,
      action.payload.engagementId
    );
    
    // Close modal and refresh the engagement submissions list after delete
    action.payload.setModalOpen(false);
    yield put(getEngagementSubmissionsSagaAction());
  } catch (e: any) {
    console.log(e);
  } finally {
    // Clear loading state for this specific engagement
    yield put(engagementFormSliceAction.setDeleteLoading({
      engagementId: action.payload.engagementId,
      loading: false
    }));
  }
}

function* engagementFormSaga() {
  yield takeLatest(
    engagementFormSagaActionTypes.callGetFormFieldsEngagementSaga,
    getFormFieldsEngagementSaga
  );
  yield takeLatest(
    engagementFormSagaActionTypes.callSubmitFormEngagementSaga,
    submitFormEngagementSaga
  );
  yield takeLatest(
    engagementFormSagaActionTypes.callUpdateFormEngagementSaga,
    updateFormEngagementSaga
  );
  yield takeLatest(
    engagementFormSagaActionTypes.callGetEngagementSubmissionsSaga,
    getEngagementSubmissionsSaga
  );
  yield takeLatest(
    engagementFormSagaActionTypes.callGetEngagementByIdSaga,
    getEngagementByIdSaga
  );
  yield takeLatest(
    engagementFormSagaActionTypes.callReviewEngagementSaga,
    reviewEngagementSaga
  );
  yield takeLatest(
    engagementFormSagaActionTypes.callDeleteEngagementSaga,
    deleteEngagementSaga
  );
}

export default engagementFormSaga;
